import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  FileText,
  Download,
  CheckCircle,
  Loader2,
  X,
} from 'lucide-react';
import { Card, Button } from '@azyntor-labs/nexus-ui';
import { toast } from '@/utils/toast';
import { apiClient } from '@/api/client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

interface Props {
  examId: string;
}

export const CSVImportUploader = ({ examId }: Props) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Import mutation — uses the shared apiClient so auth (access token +
  // CSRF) is injected automatically by the SDK's attached interceptors.
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(
        `/exams/${examId}/import-enrollments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments', examId] });
      queryClient.invalidateQueries({ queryKey: ['students-search'] });
      setImportResult(data.data);
      toast.success(data.message);
      setSelectedFile(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to import CSV');
    },
  });

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    setSelectedFile(file);
    setImportResult(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API_BASE_URL}/exams/${examId}/enrollment-template`, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Bulk Import via CSV</h3>
          <p className="text-sm text-gray-400">Upload a CSV file to enroll multiple students at once</p>
        </div>
        <Button
          onClick={handleDownloadTemplate}
          leftIcon={<Download className="w-4 h-4" />}
          variant="ghost"
          size="sm"
          className="text-cyan-400 hover:text-cyan-300"
        >
          Download Template
        </Button>
      </div>

      {/* Upload Area */}
      <Card className="bg-[#12121a]/80 border-white/[0.06] p-6">
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${isDragging
              ? 'border-teal-400 bg-teal-500/10'
              : 'border-white/[0.12] hover:border-teal-400/50 hover:bg-teal-500/5'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />

          {selectedFile ? (
            <div>
              <FileText className="w-12 h-12 mx-auto text-teal-400 mb-3" />
              <p className="text-white font-medium mb-1">{selectedFile.name}</p>
              <p className="text-sm text-gray-400 mb-4">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpload();
                  }}
                  disabled={importMutation.isPending}
                  leftIcon={importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  {importMutation.isPending ? 'Importing...' : 'Import Students'}
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  variant="ghost"
                  leftIcon={<X className="w-4 h-4" />}
                  className="text-gray-400"
                  disabled={importMutation.isPending}
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Upload className="w-12 h-12 mx-auto text-gray-500 mb-3" />
              <p className="text-white font-medium mb-1">Drop CSV file here or click to browse</p>
              <p className="text-sm text-gray-400">
                CSV must have an 'email' column with student email addresses
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Import Result */}
      {importResult && (
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-white mb-2">Import Complete</h4>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-400">Total Rows</p>
                  <p className="text-lg font-bold text-white">{importResult.total_rows}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Enrolled</p>
                  <p className="text-lg font-bold text-emerald-400">{importResult.successful_enrollments}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Already Enrolled</p>
                  <p className="text-lg font-bold text-cyan-400">{importResult.already_enrolled || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Not Found</p>
                  <p className="text-lg font-bold text-amber-400">{importResult.failed || 0}</p>
                </div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <p className="text-sm text-red-400 font-medium mb-2">
                    {importResult.errors.length} Error(s):
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResult.errors.slice(0, 5).map((err: any, i: number) => (
                      <p key={i} className="text-xs text-red-300">
                        Row {err.row}: {err.email} - {err.error}
                      </p>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs text-red-400">... and {importResult.errors.length - 5} more errors</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 text-xs text-gray-500">
                Completed in {importResult.duration_seconds || 0} seconds
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
